import { renderElement } from "../../utils/use-render.js";
import { useMenuOptionState } from "../menu/context.js";
import { MenuRadioGroup } from "../menu/radio-group.js";
import { useQualityOptions } from "../quality/use-quality-options.js";
import { QualityRadioGroupDataAttrs } from "@videojs/core";
import { getStateDataAttrs } from "@videojs/core/dom";
import { Fragment, createContext, forwardRef, useContext } from "react";
import { jsx } from "react/jsx-runtime";
import { isFunction } from "@videojs/utils/predicate";
//#region src/ui/quality-radio-group/component.tsx
const QualityRadioGroupContext = createContext(void 0);
/** Owns quality option state and shares it with an enclosing menu. Does not render a DOM element. */
function QualityRadioGroupRoot({ children, ...props }) {
	const quality = useQualityOptions(props);
	useMenuOptionState(toMenuOptionState(quality));
	return /* @__PURE__ */ jsx(QualityRadioGroupContext.Provider, {
		value: quality,
		children
	});
}
/** Displays the selected quality label. */
const QualityRadioGroupValue = forwardRef(function QualityRadioGroupValue({ render, className, style, ...elementProps }, forwardedRef) {
	const quality = useQualityRadioGroupContext();
	if (!quality) return null;
	return renderElement("span", {
		render,
		className,
		style
	}, {
		state: quality,
		ref: forwardedRef,
		props: [elementProps, { children: quality.selectedLabel }]
	});
});
/** Renders menu radio items for the player's video renditions. */
const QualityRadioGroupOptions = forwardRef(function QualityRadioGroupOptions(componentProps, forwardedRef) {
	const { renderItem, render, className, style, "aria-label": ariaLabelProp, "aria-labelledby": ariaLabelledBy, ...elementProps } = componentProps;
	const quality = useQualityRadioGroupContext();
	if (!quality) return null;
	const { state, value, options, setValue } = quality;
	const ariaLabel = ariaLabelProp ?? (ariaLabelledBy === void 0 ? quality.label : void 0);
	return /* @__PURE__ */ jsx(MenuRadioGroup, {
		...getStateDataAttrs(state, QualityRadioGroupDataAttrs),
		...elementProps,
		ref: forwardedRef,
		className: isFunction(className) ? className(state) : className,
		style: isFunction(style) ? style(state) : style,
		render: isFunction(render) ? (props) => render(props, state) : render,
		value,
		onValueChange: setValue,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		"aria-disabled": state.disabled || void 0,
		hidden: state.hidden,
		children: options.map((option) => {
			const itemState = {
				...option,
				checked: option.value === value
			};
			return /* @__PURE__ */ jsx(Fragment, { children: renderItem({
				value: option.value,
				disabled: option.disabled,
				"data-rendition": option.value,
				children: option.label
			}, itemState) }, option.value);
		})
	});
});
/** @internal Compatibility adapter for the existing preset sources. */
const QualityRadioGroupLegacy = forwardRef(function QualityRadioGroupLegacy({ label, formatRendition, disabled, ...props }, forwardedRef) {
	return /* @__PURE__ */ jsx(QualityRadioGroupRoot, {
		label,
		formatRendition,
		disabled,
		children: /* @__PURE__ */ jsx(QualityRadioGroupOptions, {
			...props,
			ref: forwardedRef
		})
	});
});
function useQualityRadioGroupContext() {
	const quality = useContext(QualityRadioGroupContext);
	if (quality === void 0) throw new Error("QualityRadioGroup parts must be used within QualityRadioGroup.Root");
	return quality;
}
function toMenuOptionState(quality) {
	return {
		value: quality?.selectedLabel ?? "",
		disabled: quality?.disabled ?? true,
		hidden: quality?.hidden ?? true,
		availability: quality?.state.availability ?? "unsupported"
	};
}
//#endregion
export { QualityRadioGroupLegacy, QualityRadioGroupOptions, QualityRadioGroupRoot, QualityRadioGroupValue };

//# sourceMappingURL=component.js.map